import React, { FC } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'hooks';
import ItemConfiguration from 'types/ItemConfiguration';
import Checkbox from 'components/ui/checkbox';
import Switch from 'components/ui/switch';
import Icon from 'components/ui/icon';
import Utils from 'helpers/Utils';

import { DndContext, DragEndEvent } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from '@dnd-kit/modifiers';

import './configuration.scss';
import { ComponentRegistry } from 'runtime/registry/components/ComponentRegistry';
import { resolveWlValue } from 'helpers/wlResolver';

interface Props {
  configuration: ItemConfiguration[];
  changeConfiguration: (configuration: ItemConfiguration[]) => void;
}
type SortableItemControlType = 'switch' | 'checkbox';

export const Configuration: FC<Props> = ({
  configuration,
  changeConfiguration,
}) => {
  const getTranslate = useTranslation();
  const fixedItems = configuration.filter((item) =>
    Utils.hasProp(item, 'fixed')
  );
  const sortableItems = configuration.filter(
    (item) => !Utils.hasProp(item, 'fixed')
  );
  const props = ComponentRegistry.getProps('Configuration');
  const isColumnFixDisabled = resolveWlValue((config) => config.isColumnFixDisabled, false);

  // For multiple fixed field activation if one of the lock clicked
  const handleChangeItemFixed = () => {
    if (isColumnFixDisabled) {
      return;
    }
    const newConfig = configuration.map(({ fixed, ...rest }) => {
      if (fixed !== undefined) {
        return {
          ...rest,
          fixed: !fixed,
        };
      }

      return rest;
    });

    changeConfiguration(newConfig);
  };

  const handleChangeItemActive = (id: string) => {
    const newConfig = configuration.map((item) => {
      if (item.id === id) {
        return { ...item, active: !item.active };
      }

      return item;
    });

    changeConfiguration(newConfig);
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = configuration.findIndex((item) => item.id === active.id);
    const newIndex = configuration.findIndex((item) => item.id === over.id);
    changeConfiguration(arrayMove(configuration, oldIndex, newIndex));
  };

  return (
    <div className='configuration__content'>
      <div className='configuration__list'>
        {fixedItems.map(({ id, fixed, localId }, index) => {
          return (
            <div className='configuration__item configuration__item--fixed' key={id}>
              <span className='configuration__item-position'>{index + 1}</span>
              <label
                className={classNames('ui-checkbox', 'ui-checkbox_locker', {
                  'ui-checkbox_disabled': isColumnFixDisabled,
                })}
                onClick={handleChangeItemFixed}>
                <span className='ui-checkbox__icon'>
                  <Icon
                    name={fixed ? 'im-Lock-close' : 'im-Lock-open'}
                    size={18}
                  />
                </span>
                <span className='ui-checkbox__text'>
                  {getTranslate(localId)}
                </span>
              </label>
            </div>
          );
        })}

        <div className='configuration__sortable-list'>
          <DndContext
            modifiers={[restrictToVerticalAxis, restrictToParentElement]}
            onDragEnd={handleDragEnd}>
            <SortableContext
              items={configuration}
              strategy={verticalListSortingStrategy}>
              {sortableItems.map(({ id, active, localId }, index) => {
                return (
                  <SortableItem
                    key={id}
                    id={id}
                    index={index + fixedItems.length}
                    active={active}
                    localId={localId}
                    controlType={props?.controlType}
                    onSelect={handleChangeItemActive}
                  />
                );
              })}
            </SortableContext>
          </DndContext>
        </div>
      </div>
    </div>
  );
};

const SortableItem: FC<{
  id: string;
  index: number;
  active: boolean;
  localId: string;
  onSelect: (id: string) => void;
  controlType?: SortableItemControlType;
}> = ({ id, index, active, localId, onSelect, controlType = 'checkbox' }) => {
  const getTranslate = useTranslation();

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: !active });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  const controlProps = {
    id,
    text: getTranslate(localId),
    checked: active,
    onChange: () => onSelect(id),
  };
  const ControlComponent = controlType === 'switch' ? Switch : Checkbox;

  return (
    <div
      key={id}
      ref={setNodeRef}
      className={classNames('configuration__item', {
        configuration__item_dragging: isDragging,
      })}
      style={style}
      {...attributes}>
      <span className='configuration__item-position'>{index + 1}</span>
      <div
        className={classNames('configuration__drag-handler', {
          'configuration__drag-handler_dragging': isDragging,
          'configuration__drag-handler_disabled': !active,
        })}
        {...listeners}>
        <Icon
          className='configuration__drag-handler-icon'
          name='im-Draganddrop'
          size={12}
        />
      </div>
      <ControlComponent {...controlProps} />
    </div>
  );
};